import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Zap, Eye, Users, Heart, Loader2, Info, ChevronDown, X, Check } from "lucide-react";
import ReelThumb from "../components/ReelThumb";
import { payWithRazorpay } from "../lib/razorpay";
import MyPayments from "../components/MyPayments";

/* Pretty label for any reel: title → caption/description → "Reel · <date>". */
const prettyReelLabel = (r) => {
  const t = (r?.title || "").trim();
  if (t) return t;
  const c = (r?.description || r?.caption || "").trim();
  if (c) return c.length > 48 ? c.slice(0, 48) + "…" : c;
  const ts = r?.created_at;
  try {
    if (ts) {
      const d = new Date(ts);
      if (!isNaN(d)) return `Reel · ${d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}`;
    }
  } catch { /* noop */ }
  return "Reel";
};

function ReelPicker({ reels, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => reels.find((r) => r.id === value) || null, [reels, value]);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="boost-reel-trigger"
        className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none flex items-center gap-3 active:scale-[0.99]">
        {selected ? (
          <>
            <ReelThumb reel={selected} className="w-10 h-14" iconSize="w-4 h-4"/>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-semibold truncate">{prettyReelLabel(selected)}</div>
              <div className="text-[11px] text-white/50 truncate">{selected.views ?? 0} views · {selected.likes ?? 0} likes</div>
            </div>
          </>
        ) : (
          <span className="text-white/60 flex-1 text-left">-- Choose your reel --</span>
        )}
        <ChevronDown className="w-5 h-5 text-white/60 flex-shrink-0"/>
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] max-w-md mx-auto" data-testid="boost-reel-picker">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)}/>
          <div className="absolute bottom-0 inset-x-0 bg-[#0B0F19] rounded-t-3xl border-t border-white/10 max-h-[75vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-outfit font-bold text-lg">Choose a reel to boost</h3>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="boost-reel-picker-close" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90"><X className="w-5 h-5"/></button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {reels.length === 0 && (
                <div className="text-center py-10 text-white/55 text-sm">You haven't uploaded any reels yet.</div>
              )}
              {reels.map((r) => {
                const isSel = r.id === value;
                return (
                  <button key={r.id} type="button" onClick={() => { onChange(r.id); setOpen(false); }} data-testid={`boost-reel-opt-${r.id}`}
                    className={`w-full flex items-center gap-3 p-2 rounded-xl mb-1 text-left active:scale-[0.99] ${isSel ? "bg-[#00E5FF]/15 border border-[#00E5FF]/50" : "bg-white/5 border border-transparent"}`}>
                    <ReelThumb reel={r} className="w-14 h-20"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{prettyReelLabel(r)}</div>
                      <div className="text-[11px] text-white/50 truncate mt-0.5">
                        {(r.views ?? 0).toLocaleString()} views · {(r.likes ?? 0).toLocaleString()} likes
                        {r.boosted && <span className="ml-2 text-[#00E5FF] font-bold">· BOOSTED</span>}
                      </div>
                    </div>
                    {isSel && <Check className="w-5 h-5 text-[#00E5FF] flex-shrink-0"/>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Boost() {
  const [reels, setReels] = useState([]);
  const [tiers, setTiers] = useState({});
  const [sel, setSel] = useState({ reel_id: "", boost_type: "views", tier: "1k" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [payRefresh, setPayRefresh] = useState(0);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({data})=>{
      setMe(data);
      api.get(`/users/${data.id}`).then(r => setReels(r.data.reels || []));
    });
    api.get("/boost/tiers").then(r => setTiers(r.data));
  }, []);

  const ICONS = { views: Eye, followers: Users, likes: Heart };
  const isFollowers = sel.boost_type === "followers";

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setLoading(true);
    try {
      const result = await payWithRazorpay({ kind: "boost", ...sel });
      setMsg(`✅ ${result}`);
      setPayRefresh(r => r + 1);
    } catch (e) { if (e.message !== "Payment cancelled") setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-7 h-7 text-[#00E5FF]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Boost Reel</h1>
      </div>
      <p className="text-white/60 text-sm mb-5">Reach more viewers with Likes, Followers & Views boosts</p>

      <div className="mb-4 p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex gap-2 text-xs">
        <Info className="w-4 h-4 text-[#FF7A00] flex-shrink-0 mt-0.5"/>
        <p className="text-white/80">Pay securely with <b className="text-[#3395FF]">Razorpay</b> (UPI, cards, netbanking). Your boost starts <b className="text-emerald-300">instantly</b> after payment.</p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="boost-form">
        <div>
          <label className="text-xs uppercase text-white/60 font-semibold mb-2 block">Boost Type</label>
          <div className="grid grid-cols-3 gap-2">
            {Object.keys(tiers).map(t => {
              const Icon = ICONS[t];
              return (
                <button type="button" key={t} onClick={()=>setSel(s=>({...s, boost_type:t}))} data-testid={`boost-type-${t}`}
                  className={`p-3 rounded-2xl border ${sel.boost_type===t?"border-[#00E5FF] bg-[#00E5FF]/10":"border-white/10 bg-white/5"}`}>
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${sel.boost_type===t?"text-[#00E5FF]":""}`}/>
                  <div className="text-xs font-bold capitalize">{t}</div>
                </button>
              );
            })}
          </div>
        </div>

        {isFollowers ? (
          <div data-testid="boost-profile-target">
            <label className="text-xs uppercase text-white/60 font-semibold">Your Profile</label>
            <Link to={me ? `/u/${me.id}` : "#"} data-testid="boost-profile-link"
              className="mt-1 w-full px-4 py-3 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/40 flex items-center gap-3 active:scale-[0.99]">
              <img src={me?.avatar} alt="" className="w-12 h-12 rounded-full bg-white/10"/>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-bold truncate" data-testid="boost-profile-username">@{me?.username || "…"}</div>
                <div className="text-[11px] text-white/60 truncate">{me?.name}{me?.hifi_id ? ` · Hifi ID ${me.hifi_id}` : ""}</div>
                <div className="text-[11px] text-[#00E5FF] truncate" data-testid="boost-profile-url">hifireels.com/u/{me?.id}</div>
              </div>
              <Check className="w-5 h-5 text-[#00E5FF] flex-shrink-0"/>
            </Link>
            <p className="text-[11px] text-white/50 mt-1.5">New followers are added to your profile, not to a reel.</p>
          </div>
        ) : (
          <div>
            <label className="text-xs uppercase text-white/60 font-semibold">Select Reel</label>
            <ReelPicker reels={reels} value={sel.reel_id} onChange={(rid) => setSel(s => ({ ...s, reel_id: rid }))}/>
          </div>
        )}

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold mb-2 block">Tier</label>
          <div className="grid grid-cols-2 gap-2">
            {["1k", "10k"].map(tk => (
              <button type="button" key={tk} onClick={()=>setSel(s=>({...s, tier:tk}))} data-testid={`boost-tier-${tk}`}
                className={`p-3 rounded-2xl border ${sel.tier===tk?"border-[#FF2A5F] bg-[#FF2A5F]/10":"border-white/10 bg-white/5"}`}>
                <div className="font-outfit font-bold text-lg">{tk.toUpperCase()}</div>
                <div className="text-xs text-[#FF7A00] font-bold">₹{tiers[sel.boost_type]?.[tk] ?? "-"}</div>
              </button>
            ))}
          </div>
        </div>

        {msg && <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="boost-msg">{msg}</div>}
        {err && <div className="p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 text-[#FF2A5F] text-sm" data-testid="boost-err">{err}</div>}

        <button data-testid="boost-submit" disabled={loading || (!isFollowers && !sel.reel_id)} className="w-full py-3.5 rounded-full bg-[#00E5FF] text-black font-outfit font-bold neon-cyan active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Zap className="w-4 h-4"/>Pay & Boost • ₹{tiers[sel.boost_type]?.[sel.tier] ?? "-"}</>}
        </button>
      </form>
      <MyPayments kind="boost" refreshKey={payRefresh}/>
    </div>
  );
}
